import { Button, Card, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useImperativeHandle, useRef } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { usePurchaseItems } from '../hooks/usePurchaseItems';
import { IPurchase, IPurchaseItem } from '../types/purchase';
import Table from './table';
import PurchaseItemFormModal, { IPurchaseItemFormModalInstance } from './purchase-item-form-modal';
import { USER_ROLE_MODERATOR_LIMITED, useUser } from '../hooks/useUser';

interface IPurchaseItemsTableProps {
    purchase: IPurchase;
    onFormHide: () => void;
}

export interface IPurchaseItemsTableRef {
    update: () => void;
}

function PurchaseItemsTable({ purchase, onFormHide }: IPurchaseItemsTableProps, ref: any) {
    const {
        data, deleteRow, getData, loading,
    } = usePurchaseItems(purchase.id);
    const { t } = useTranslation();
    const itemFormModal = useRef<IPurchaseItemFormModalInstance>();
    const user = useUser({ canFetch: false, canLogoutBy401: false, showFetchMessages: false });

    const limitedAccess = user.info?.role_value === USER_ROLE_MODERATOR_LIMITED;

    const onHide = () => {
        getData();
        onFormHide();
    };

    useImperativeHandle(ref, (): IPurchaseItemsTableRef => ({
        update: getData,
    }));

    const actions = (row: IPurchaseItem) => (
        <Space>
            <Button
                icon={<EditOutlined />}
                type="link"
                onClick={() => itemFormModal.current?.open(purchase, row)}
            />
            <Button
                hidden={limitedAccess}
                icon={<DeleteOutlined />}
                type="link"
                onClick={() => deleteRow(row.id)}
            />
        </Space>
    );

    const extra = (
        <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => itemFormModal.current?.open(purchase)}
        />
    );

    return (
        <Card extra={limitedAccess ? undefined : extra} title={t('goodsList')}>
            <Table
                loading={loading}
                dataSource={data?.list}
                pagination={{
                    current: data?.currentPage,
                    total: data?.total,
                    pageSize: data?.perPage,
                    hideOnSinglePage: true,
                }}
                columns={[{
                    dataIndex: 'name',
                    title: t('goodName'),
                }, {
                    align: 'center',
                    render: actions,
                    width: 100,
                    fixed: 'right',
                }]}
            />
            <PurchaseItemFormModal ref={itemFormModal} onHide={onHide} />
        </Card>
    );
}

export default React.forwardRef(PurchaseItemsTable);
